
import { Component, Prop, Vue, Emit, Watch } from 'vue-property-decorator'
import { GetLeakStatusColor } from '@/utils/statusUi'
import { formatDate } from '@/utils/index'
import { UserModule } from '@/store/modules/user'
import MapNavigation from '@/components/MapNavigation/index.vue'
import LeakStatusTag from '../LeakStatusTag/index.vue'
import { ILeakPoint, LeakStatus, IAllUserInfo, GetLeakStatus } from '@/api/types'
import { CancelReport, AssignRepairDept, CancelAssignRepairDept, AssignRepairUser, CancelAssignRepairUser, CancelEndRepair, DeleteLeak, MarkAsInternalRepair } from '@/api/leaks'
import { detectMiniProgram } from '@/utils/check'
@Component({
  name: 'LeakBriefInfoCard',
  components: {
    MapNavigation,
    LeakStatusTag
  }
})
export default class extends Vue {
  @Prop({ required: true }) private leak!: ILeakPoint
  @Prop({ default: true }) private showStatus!:boolean
  @Prop({ default: false }) private isMobile!: boolean
  @Prop({ default: false }) private selectable!: boolean
  @Prop({ default: false }) private selected! :boolean
  @Prop({ default: false }) private selectDisabled! :boolean
  @Prop({ default: [] }) private userList!: IAllUserInfo[]

  // 本地 data 属性
  private localUserList: IAllUserInfo[] = []
  private localIsMobile = false
  private localShowStatus = false
  private localSelectable = false
  private localSelected = false
  private localSelectDisabled = false

  // 新增操作相关属性
  private saving = false

  // 使用普通对象替代const enum，避免编译错误
  private leakStatusEnum = {
    Unreported: LeakStatus.Unreported,
    NeedAssignRepairDept: LeakStatus.NeedAssignRepairDept,
    NeedAssignRepairUser: LeakStatus.NeedAssignRepairUser,
    Repairing: LeakStatus.Repairing,
    Repaired: LeakStatus.Repaired,
    CantRepair: LeakStatus.CantRepair,
    NoNeedRepair: LeakStatus.NoNeedRepair
  }

  // 权限相关计算属性
  get canEidtDetection(): boolean {
    return this.hasPermission(["上帝", "漏点管理", "漏点检测"])
  }

  get canEditRepair(): boolean {
    return this.hasPermission(["上帝", "漏点管理", "漏点维修"])
  }

  get canAssign(): boolean {
    return this.hasPermission(['上帝','漏点管理'])
  }

  get canClaim(): boolean {
    return this.hasPermission(['上帝','漏点管理','漏点维修'])
  }

  get canReport(): boolean {
    return this.hasPermission(['上帝','漏点管理','漏点检测'])
  }

  get canCancelReport(): boolean {
    return this.hasPermission(['上帝','漏点管理','漏点检测'])
  }

  get canAssignRepairDept(): boolean {
    return this.hasPermission(['上帝','漏点管理'])
  }

  get canCancelAssignRepairDept(): boolean {
    return this.hasPermission(['上帝','漏点管理'])
  }

  get canAssignRepairUser(): boolean {
    return this.hasPermission(['上帝','漏点管理','漏点维修'])
  }

  get canCancelAssignRepairUser(): boolean {
    return this.hasPermission(['上帝','漏点管理','漏点维修'])
  }

  get canFinishRepair(): boolean {
    return this.hasPermission(['上帝','漏点管理','漏点维修'])
  }

  // 操作按钮显示控制
  get showEditDetectionBtn(): boolean {
    if (!this.leak) return false

    if (this.leak.leakStatus === LeakStatus.Unreported ||
        this.leak.leakStatus === LeakStatus.NeedAssignRepairDept) {
      return this.canEidtDetection
    }
    return false
  }

  get showDeleteBtn(): boolean {
    if (!this.leak) return false

    if (this.leak.leakStatus === LeakStatus.Unreported) {
      return this.canEidtDetection
    }
    return false
  }

  get showReportBtn(): boolean {
    if (!this.leak) return false

    if (this.leak.leakStatus === LeakStatus.Unreported) {
      return this.canReport
    }
    return false
  }

  get showCancelReportBtn(): boolean {
    if (!this.leak) return false

    if (this.leak.leakStatus === LeakStatus.NeedAssignRepairDept) {
      return this.canCancelReport
    }
    return false
  }

  get showAssignBtn(): boolean {
    if (!this.leak) return false

    if (this.leak.leakStatus === LeakStatus.NeedAssignRepairDept) {
      return this.canAssignRepairDept
    }
    return false
  }

  get showInternalRepairBtn(): boolean {
    if (!this.leak) return false

    if (this.leak.leakStatus === LeakStatus.NeedAssignRepairDept) {
      return this.canAssignRepairDept
    }
    return false
  }

  get showCancelAssignBtn(): boolean {
    if (!this.leak) return false

    if (this.leak.leakStatus === LeakStatus.NeedAssignRepairUser) {
      return this.canCancelAssignRepairUser
    }
    return false
  }

  get showClaimBtn(): boolean {
    if (!this.leak) return false

    if (this.leak.leakStatus === LeakStatus.NeedAssignRepairUser) {
      return this.canAssignRepairUser
    }
    return false
  }

  get showCancelClaimBtn(): boolean {
    if (!this.leak) return false

    if (this.leak.leakStatus === LeakStatus.Repairing) {
      return this.canCancelAssignRepairUser
    }
    return false
  }

  get showFinishBtn(): boolean {
    if (!this.leak) return false

    if (this.leak.leakStatus === LeakStatus.Repairing) {
      return this.canFinishRepair
    }
    return false
  }

  get showEditRepairBtn(): boolean {
    if (!this.leak) return false

    if (this.leak.leakStatus === LeakStatus.Repairing) {
      return this.canEditRepair
    }
    return false
  }

  get showCancelRepairBtn(): boolean {
    if (!this.leak) return false

    if (this.leak.leakStatus === LeakStatus.Repaired ||
        this.leak.leakStatus === LeakStatus.NoNeedRepair ||
        this.leak.leakStatus === LeakStatus.CantRepair) {
      return this.canFinishRepair
    }
    return false
  }

  private showMapNavigation = false
  private navigationEndPosition: [number, number] | null = null
  private navigationEndAddress = ''
  private isMiniProgram = false

  created() {
    // 初始化本地数据
    this.localUserList = [...this.userList]
    this.localIsMobile = this.isMobile
    this.localShowStatus = this.showStatus
    this.localSelectable = this.selectable
    this.localSelected = this.selected
    this.localSelectDisabled = this.selectDisabled
  }

  // 监听 userList 变化
  @Watch('userList', { deep: true })
  onUserListChange(newUserList: IAllUserInfo[]) {
    this.localUserList = [...newUserList]
  }

  // 监听 isMobile 变化
  @Watch('isMobile')
  onIsMobileChange(newIsMobile: boolean) {
    this.localIsMobile = newIsMobile
  }

  // 监听 showStatus 变化
  @Watch('showStatus')
  onShowStatusChange(newShowStatus: boolean) {
    this.localShowStatus = newShowStatus
  }

  // 监听 selectable 变化
  @Watch('selectable')
  onSelectableChange(newSelectable: boolean) {
    this.localSelectable = newSelectable
  }

  // 监听 selected 变化
  @Watch('selected')
  onSelectedChange(newSelected: boolean) {
    this.localSelected = newSelected
  }

  // 监听 selectDisabled 变化
  @Watch('selectDisabled')
  onSelectDisabledChange(newSelectDisabled: boolean) {
    this.localSelectDisabled = newSelectDisabled
  }

  mounted() {
    // 检测是否在小程序环境中
    this.detectMiniProgram()
  }

  /**
   * 检测是否在小程序环境中
   */
  private detectMiniProgram(): void {
    // 使用 window.wx 和 window.wx.miniProgram 检测小程序环境
    // 或者使用其他小程序特定的环境变量
    this.isMiniProgram = detectMiniProgram()
  }

  private getCheckUserName(leak: ILeakPoint): string {
    if (!leak.detection) return '-'
    const checkUser = this.localUserList.find(
      user => user.user.id === leak.detection?.checkUserId)
    return checkUser ? checkUser.user.username: '-'
  }

  private formatDate(date: string | number | Date): string {
    // 格式化时间
    return formatDate(date)
  }

  private hasPermission(permissions: string[]): boolean {
    return UserModule.hasPermissions(permissions)
  }

  private getFieldValue(row: ILeakPoint, field: string): string {
    // 获取字段值，处理可能的嵌套或空值
    if (!row) return '-'

    // 根据漏点状态判断是否应该优先展示repair数据
    const isRepairStatus = row.leakStatus === this.leakStatusEnum.Repairing ||
                          row.leakStatus === this.leakStatusEnum.Repaired ||
                          row.leakStatus === this.leakStatusEnum.CantRepair ||
                          row.leakStatus === this.leakStatusEnum.NoNeedRepair
    const hasRepair = !!row.repair
    const hasDetection = !!row.detection

    if (field === 'diameter') {
      // 管径优先显示repair中的size
      if (isRepairStatus && hasRepair && row.repair && row.repair.size !== undefined) {
        return `${row.repair.size}mm`
      } else if (hasDetection && row.detection && row.detection.size !== undefined) {
        return `${row.detection.size}mm`
      }
      return '-'
    } else if (field === 'pipeMaterial') {
      // 管材优先显示repair中的material
      if (isRepairStatus && hasRepair && row.repair && row.repair.material) {
        return row.repair.material
      } else if (hasDetection && row.detection && row.detection.material) {
        return row.detection.material
      }
      return '-'
    } else if (field === 'leakVolume') {
      // 漏量优先显示repair中的leakLeakage
      if (isRepairStatus && hasRepair && row.repair && row.repair.leakLeakage !== undefined) {
        return `${row.repair.leakLeakage}L/min`
      }
      return '-'
    }

    return '-'
  }

  @Emit('detail')
  private handleDetail(): ILeakPoint {
    return this.leak
  }

  private handleOperation(type: string): void {
    // 根据操作类型执行不同的操作
    switch (type) {
      case 'report':
        this.handleReport();
        break;
      case 'detection':
        this.goToEditPage(type);
        break;
      case 'repair':
        this.goToEditPage(type);
        break;
      case 'finish':
        this.goToEditPage(type);
        break;
      case 'delete':
        this.handleDelete();
        break;
      case 'cancel-report':
        this.handleCancelReport();
        break;
      case 'assign':
        this.handleAssign();
        break;
      case 'internal-repair':
        this.handleMarkInternalRepair();
        break;
      case 'cancel-assign':
        this.handleCancelAssign();
        break;
      case 'claim':
        this.handleClaim();
        break;
      case 'cancel-claim':
        this.handleCancelClaim();
        break;
      case 'cancel-repair':
        this.handleCancelRepair();
        break;
      default:
        console.error('未知操作类型:', type);
        break;
    }
  }

  @Emit('select')
  private handleSelect(val: boolean): [string | number, boolean] {
    return [this.leak.id, val]
  }

  /**
   * 处理上报
   */
  private async handleReport() {
    try {
      this.saving = true;
      // 在实际实现中，应该导航到上报页面
      this.goToEditPage('report');
    } catch (error) {
      console.error('上报操作失败', error);
      this.$message.error('上报操作失败');
    } finally {
      this.saving = false;
    }
  }

  /**
   * 跳转到编辑页面
   */
  private goToEditPage(mode: string) {
    // 构建路由参数
    const query: Record<string, string> = {
      mode: mode
    };

    // 根据不同模式跳转到不同页面
    if (mode === 'detection' || mode === 'report') {
      this.$router.push({
        name: 'LeakEditDetection',
        params: {
          id: this.leak.id.toString(),
          ...query
        }
      });
    } else if (mode === 'repair' || mode === 'finish') {
      this.$router.push({
        name: 'LeakEditRepair',
        params: {
          id: this.leak.id.toString(),
          ...query
        }
      });
    }
  }

  /**
   * 删除漏点
   */
  private handleDelete() {
    this.$confirm('确认删除该漏点？此操作不可逆！', '警告', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(async () => {
      this.saving = true;
      try {
        await DeleteLeak({ id: this.leak.id || -1 });
        this.$message.success('删除成功');

        // 刷新页面或通知父组件刷新列表
        this.$emit('refresh');
      } catch (error) {
        console.error('删除漏点失败', error);
        this.$message.error('删除漏点失败');
      } finally {
        this.saving = false;
      }
    }).catch(() => {
      // 用户取消删除
    });
  }

  /**
   * 撤销上报
   */
  private handleCancelReport() {
    this.$confirm('确认撤销上报该漏点？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(async () => {
      this.saving = true;
      try {
        await CancelReport({ id: this.leak.id || -1 });
        this.$message.success('撤销上报成功');

        // 刷新页面或通知父组件刷新列表
        this.$emit('refresh');
      } catch (error) {
        console.error('撤销上报失败', error);
        this.$message.error('撤销上报失败');
      } finally {
        this.saving = false;
      }
    }).catch(() => {
      // 用户取消操作
    });
  }

  /**
   * 处理分配
   */
  @Emit('operation')
  private handleAssign(): { id: string | number, type: string } {
    return { id: this.leak.id, type: 'assign' };
  }

  /**
   * 内部维修
   */
  private async handleMarkInternalRepair() {
    try {
      this.saving = true;
      await MarkAsInternalRepair({ id: this.leak.id });
      this.$message.success('标记内部维修成功');

      // 刷新页面或通知父组件刷新列表
      this.$emit('refresh');
    } catch (error) {
      console.error('标记内部维修失败', error);
      this.$message.error('标记内部维修失败');
    } finally {
      this.saving = false;
    }
  }

  /**
   * 撤销分配
   */
  private handleCancelAssign() {
    this.$confirm('确认撤销分配维修部门？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(async () => {
      this.saving = true;
      try {
        await CancelAssignRepairDept({ id: this.leak.id || -1 });
        this.$message.success('撤销分配成功');

        // 刷新页面或通知父组件刷新列表
        this.$emit('refresh');
      } catch (error) {
        console.error('撤销分配失败', error);
        this.$message.error('撤销分配失败');
      } finally {
        this.saving = false;
      }
    }).catch(() => {
      // 用户取消操作
    });
  }

  /**
   * 认领漏点
   */
  private handleClaim() {
    this.$confirm('确认认领该漏点？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(async () => {
      this.saving = true;
      try {
        await AssignRepairUser({
          id: this.leak.id || -1,
          userId: UserModule.userInfo?.id || -1 // 使用当前登录用户ID
        });
        this.$message.success('认领成功');

        // 刷新页面或通知父组件刷新列表
        this.$emit('refresh');
      } catch (error) {
        console.error('认领漏点失败', error);
        this.$message.error('认领漏点失败');
      } finally {
        this.saving = false;
      }
    }).catch(() => {
      // 用户取消认领
    });
  }

  /**
   * 撤销认领
   */
  private handleCancelClaim() {
    this.$confirm('确认撤销认领该漏点？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(async () => {
      this.saving = true;
      try {
        await CancelAssignRepairUser({ id: this.leak.id || -1 });
        this.$message.success('撤销认领成功');

        // 刷新页面或通知父组件刷新列表
        this.$emit('refresh');
      } catch (error) {
        console.error('撤销认领失败', error);
        this.$message.error('撤销认领失败');
      } finally {
        this.saving = false;
      }
    }).catch(() => {
      // 用户取消操作
    });
  }

  /**
   * 撤销维修
   */
  private handleCancelRepair() {
    this.$confirm('确认撤销维修结果？漏点将恢复到维修中状态。', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(async () => {
      this.saving = true;
      try {
        await CancelEndRepair({ id: this.leak.id || -1 });
        this.$message.success('撤销维修成功');

        // 刷新页面或通知父组件刷新列表
        this.$emit('refresh');
      } catch (error) {
        console.error('撤销维修失败', error);
        this.$message.error('撤销维修失败');
      } finally {
        this.saving = false;
      }
    }).catch(() => {
      // 用户取消操作
    });
  }

  private handleNavigation(event?: Event): void {
    // 如果传入了事件对象，阻止事件冒泡
    if (event) {
      event.stopPropagation()
    }

    // 获取漏点位置信息
    let latitude: number | undefined
    let longitude: number | undefined

    // 从漏点信息中提取经纬度坐标（尝试多种可能的数据结构）
    if (this.leak.detection && this.leak.detection.location &&
        this.leak.detection.location.latitude && this.leak.detection.location.longitude) {
      // 结构1: leak.detection.location.latitude/longitude
      latitude = this.leak.detection.location.latitude
      longitude = this.leak.detection.location.longitude
    }

    console.log("导航信息 - 经纬度:", longitude, latitude)

    if (latitude && longitude) {
      // 设置导航终点位置
      this.navigationEndPosition = [longitude, latitude]

      // 提取地址信息（优先使用参考地址，其次使用详细地址）
      let address = ''
      if (this.leak.detection && this.leak.detection.referenceAddr) {
        address = this.leak.detection.referenceAddr
      } else if (this.leak.detection && this.leak.detection.detailAddr) {
        address = this.leak.detection.detailAddr
      }

      console.log("导航信息 - 地址:", address)

      // 设置导航终点地址
      this.navigationEndAddress = address

      // 显示地图导航组件
      this.showMapNavigation = true
    } else {
      // 没有位置信息
      this.$message.warning('无法获取漏点位置信息')
    }
  }

  private toggleSelect(): void {
    this.localSelected = !this.localSelected
    this.$emit('select', this.leak.id, this.localSelected)
  }
}
